<template>
  <HrPageLayout
    title="Employee Clearance"
    subtitle="Digital exit clearance, liabilities and final HR verification"
    icon="📋"
    header-tone="institutional"
    :breadcrumb-items="[
      { label: 'HR', route: '/dashboard' },
      { label: 'Workforce Movement' },
      { label: 'Employee Clearance' }
    ]"
  >
    <template #header-actions>
      <div class="clr-header-actions">
        <button type="button" class="hr-btn hr-btn-success" @click="openWizard">
          <i class="bx bx-plus-circle"></i> New clearance
        </button>
        <button type="button" class="hr-btn hr-btn-secondary" :disabled="loading" @click="refreshAll">
          <i class="bx bx-refresh"></i> Refresh
        </button>
      </div>
    </template>

    <div class="clr-tabs">
      <button v-for="t in tabs" :key="t.id" type="button" class="clr-tab" :class="{ on: view === t.id }" @click="switchView(t.id)">
        <i :class="t.icon"></i> {{ t.label }}
      </button>
    </div>

    <!-- DASHBOARD -->
    <div v-if="view === 'dashboard'" class="clr-section">
      <div class="clr-metrics">
        <div v-for="m in metricCards" :key="m.key" class="clr-metric">
          <span class="lbl">{{ m.label }}</span>
          <strong :class="m.tone">{{ formatMetric(stats[m.key], m.money) }}</strong>
        </div>
      </div>

      <div v-if="viewerName" class="clr-card hr-mb-3">
        <div class="clr-card-head">
          <h4>Logged in as</h4>
        </div>
        <p class="clr-muted">{{ viewerName }} — use <strong>My tasks</strong> for cases at your stage. Director HR uses this dashboard to watch every stage and send delay alerts.</p>
      </div>

      <div class="clr-card hr-mb-3">
        <div class="clr-card-head"><h4>Clearance pipeline (serial stages)</h4></div>
        <p class="clr-muted hr-small">Flow: Dept Accountant → Procurement → Dept HR → Head of Section → Accounting Officer → CO Housing → Director SCM → CO Finance → Head of Accounts → PSB CEO → PSB Chair → Payroll Manager → Director HR (final) → Separation</p>
        <div class="clr-pipeline">
          <div
            v-for="p in pipeline"
            :key="p.stage_code"
            class="clr-pipe-step"
            :class="{ hot: Number(p.pending_count) > 0, overdue: Number(p.overdue_count) > 0 }"
          >
            <div class="pipe-name">{{ p.stage_name }}</div>
            <div class="pipe-counts">
              <span>{{ Number(p.pending_count) || 0 }} open</span>
              <span v-if="Number(p.overdue_count) > 0" class="bad">{{ p.overdue_count }} overdue</span>
            </div>
          </div>
        </div>
      </div>

      <div class="clr-card hr-mb-3">
        <div class="clr-card-head">
          <h4>Where cases are stuck</h4>
          <span v-if="canAlert" class="sub">Director HR can send delay alerts</span>
        </div>
        <div v-if="!stuckCases.length" class="clr-empty">No stuck stages.</div>
        <div v-else class="table-wrap">
          <table class="clr-table">
            <thead>
              <tr>
                <th>Case</th><th>Employee</th><th>Dept</th><th>Stage</th><th>Days</th><th>Due</th><th></th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="s in stuckCases" :key="s.stage_id || (s.id + '-' + s.stage_status)">
                <td class="mono">{{ s.clearance_number }}</td>
                <td>{{ s.employee_name }}<div class="sub">{{ s.emp_no }}</div></td>
                <td>{{ s.department_name || '—' }}</td>
                <td>
                  {{ s.current_stage_name || s.stage_status }}
                  <div class="sub">{{ s.assigned_role || '' }}</div>
                </td>
                <td :class="{ overdue: Number(s.days_at_stage) >= 5 }">{{ s.days_at_stage ?? '—' }}</td>
                <td :class="{ overdue: isOverdue(s.due_at) }">{{ s.due_at || '—' }}</td>
                <td class="clr-actions-cell">
                  <button type="button" class="link" @click="openCase(s.id, s.stage_id)">Open</button>
                  <button
                    v-if="canAlert"
                    type="button"
                    class="link warn"
                    :disabled="alertingId === s.id"
                    @click="sendDelayAlert(s)"
                  >Alert</button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <div class="clr-card">
        <div class="clr-card-head"><h4>Recent cases</h4></div>
        <div v-if="!recent.length" class="clr-empty">No cases yet.</div>
        <table v-else class="clr-table">
          <thead>
            <tr>
              <th>Clearance #</th><th>Employee</th><th>Dept</th><th>Exit</th><th>Status</th><th>Stage</th><th></th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="c in recent" :key="c.id">
              <td class="mono">{{ c.clearance_number }}</td>
              <td>{{ c.employee_name }}<div class="sub">{{ c.emp_no }}</div></td>
              <td>{{ c.department_name || '—' }}</td>
              <td>{{ formatExit(c.exit_type) }}</td>
              <td><span class="badge" :class="statusClass(c.overall_status)">{{ formatStatus(c.overall_status) }}</span></td>
              <td>{{ c.current_stage_name || '—' }}</td>
              <td><button type="button" class="link" @click="openCase(c.id)">Open</button></td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <!-- LIST -->
    <div v-if="view === 'list'" class="clr-section">
      <div class="clr-card">
        <div class="clr-filters">
          <input v-model="filters.search" class="inp" placeholder="Search name, PF no, clearance #" @keyup.enter="loadCases" />
          <select v-model="filters.status" class="inp" @change="loadCases">
            <option value="">All statuses</option>
            <option v-for="s in statusOptions" :key="s" :value="s">{{ formatStatus(s) }}</option>
          </select>
          <select v-model="filters.exitType" class="inp" @change="loadCases">
            <option value="">All exit types</option>
            <option v-for="e in exitTypes" :key="e.code" :value="e.code">{{ e.name }}</option>
          </select>
          <label class="chk"><input v-model="filters.overdueOnly" type="checkbox" @change="loadCases" /> Overdue only</label>
          <button type="button" class="hr-btn hr-btn-primary" @click="loadCases">Filter</button>
        </div>
        <div v-if="loading" class="clr-empty">Loading…</div>
        <div v-else-if="!cases.length" class="clr-empty">No clearance cases match filters.</div>
        <div v-else class="table-wrap">
          <table class="clr-table">
            <thead>
              <tr>
                <th>Clearance #</th><th>Employee</th><th>PF No</th><th>Department</th><th>Exit type</th>
                <th>Last working</th><th>Stage</th><th>Status</th><th>Days</th><th>SLA</th><th></th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="c in cases" :key="c.id">
                <td class="mono">{{ c.clearance_number }}</td>
                <td>{{ c.employee_name }}</td>
                <td>{{ c.emp_no }}</td>
                <td>{{ c.department_name || '—' }}</td>
                <td>{{ formatExit(c.exit_type) }}</td>
                <td>{{ c.last_working_date || '—' }}</td>
                <td>{{ c.current_stage_name || '—' }}</td>
                <td><span class="badge" :class="statusClass(c.overall_status)">{{ formatStatus(c.overall_status) }}</span></td>
                <td>{{ c.days_pending ?? '—' }}</td>
                <td>
                  <span v-if="Number(c.overdue_stages) > 0" class="badge bad">Overdue</span>
                  <span v-else class="badge ok">OK</span>
                </td>
                <td><button type="button" class="link" @click="openCase(c.id)">Open</button></td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>

    <!-- TASKS -->
    <div v-if="view === 'tasks'" class="clr-section">
      <div class="clr-card">
        <div class="clr-card-head">
          <h4>My clearance tasks</h4>
          <span v-if="viewerName" class="sub">Officer: {{ viewerName }}</span>
        </div>
        <p class="clr-muted hr-small">Open each employee, fill the required section for your stage, then submit to send the case to the next level.</p>
        <div v-if="!tasks.length" class="clr-empty">No assigned pending stages for your profile.</div>
        <table v-else class="clr-table">
          <thead>
            <tr><th>Case</th><th>Employee</th><th>Stage</th><th>Days waiting</th><th>Due</th><th>Status</th><th></th></tr>
          </thead>
          <tbody>
            <tr v-for="t in tasks" :key="t.id">
              <td class="mono">{{ t.clearance_number }}</td>
              <td>{{ t.employee_name }}<div class="sub">{{ t.emp_no }} · {{ t.department_name || '' }}</div></td>
              <td>{{ t.stage_name }}</td>
              <td :class="{ overdue: Number(t.days_waiting) >= 5 }">{{ t.days_waiting ?? '—' }}</td>
              <td :class="{ overdue: isOverdue(t.due_at) }">{{ t.due_at || '—' }}</td>
              <td><span class="badge" :class="statusClass(t.status)">{{ formatStatus(t.status) }}</span></td>
              <td>
                <button type="button" class="hr-btn hr-btn-primary hr-btn-sm" @click="openCase(t.case_id, t.id)">Open &amp; fill</button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <!-- REPORTS -->
    <div v-if="view === 'reports'" class="clr-section">
      <div class="clr-card">
        <div class="clr-filters">
          <select v-model="reportType" class="inp">
            <option value="status">Case status</option>
            <option value="ageing">Ageing</option>
            <option value="overdue">Overdue stages</option>
            <option value="tasks">Outstanding tasks</option>
            <option value="liability_register">Liability register</option>
            <option value="final_register">Final clearance register</option>
            <option value="exit_type">Exit type summary</option>
            <option value="performance">Departmental performance</option>
            <option value="audit">Audit report</option>
          </select>
          <button type="button" class="hr-btn hr-btn-primary" :disabled="reportLoading" @click="loadReport">Load</button>
          <button type="button" class="hr-btn hr-btn-secondary" :disabled="!reportRows.length" @click="exportReportCsv">CSV</button>
          <button type="button" class="hr-btn hr-btn-primary" :disabled="!reportRows.length || reportLoading" @click="exportReportPdf">PDF</button>
        </div>
        <div v-if="reportLoading" class="clr-empty">Loading report…</div>
        <div v-else-if="!reportRows.length" class="clr-empty">No data. Choose a report type and load.</div>
        <div v-else class="table-wrap">
          <table class="clr-table compact">
            <thead>
              <tr><th v-for="h in reportHeaders" :key="h">{{ h }}</th></tr>
            </thead>
            <tbody>
              <tr v-for="(r, i) in reportRows" :key="i">
                <td v-for="h in reportHeaders" :key="h">{{ r[h] }}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>

    <!-- CONFIG -->
    <div v-if="view === 'config' && canConfigure" class="clr-section">
      <div class="clr-card">
        <div class="clr-card-head"><h4>Stage configuration</h4></div>
        <table class="clr-table">
          <thead>
            <tr><th>Stage</th><th>Group</th><th>Parallel</th><th>SLA days</th><th>Role</th><th>Mandatory</th><th>Active</th><th></th></tr>
          </thead>
          <tbody>
            <tr v-for="s in configStages" :key="s.id">
              <td>{{ s.stage_name }}</td>
              <td>{{ s.stage_group }}</td>
              <td><input v-model="s.parallel_group" class="inp sm" /></td>
              <td><input v-model.number="s.sla_days" type="number" min="1" class="inp sm" style="width:70px" /></td>
              <td><input v-model="s.responsible_role" class="inp sm" /></td>
              <td><input v-model="s.is_mandatory" type="checkbox" true-value="1" false-value="0" /></td>
              <td><input v-model="s.is_active" type="checkbox" true-value="1" false-value="0" /></td>
              <td><button type="button" class="link" @click="saveStageConfig(s)">Save</button></td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <!-- CASE WORKSPACE -->
    <div v-if="view === 'case' && activeCase" class="clr-section">
      <div class="clr-case-top">
        <button type="button" class="hr-btn hr-btn-secondary hr-btn-sm" @click="view = 'list'"><i class="bx bx-arrow-back"></i> Back</button>
        <div class="grow">
          <h3>{{ activeCase.clearance_number }} · {{ activeCase.employee_name }}</h3>
          <p class="sub">{{ activeCase.emp_no }} · {{ activeCase.department_name }} · {{ formatExit(activeCase.exit_type) }}</p>
        </div>
        <span class="badge lg" :class="statusClass(activeCase.overall_status)">{{ formatStatus(activeCase.overall_status) }}</span>
        <button v-if="canSubmit && activeCase.overall_status === 'draft'" type="button" class="hr-btn hr-btn-primary" @click="submitCase">Submit workflow</button>
        <button v-if="canFinal && ['awaiting_final_hr','in_cpsb','in_specialist'].includes(activeCase.overall_status)" type="button" class="hr-btn hr-btn-success" @click="runFinal">Final verify</button>
        <button v-if="activeCase.certificate_number" type="button" class="hr-btn hr-btn-outline-primary" @click="downloadCertificate">Certificate</button>
        <button v-if="canReopen && ['cleared','closed','cancelled'].includes(activeCase.overall_status)" type="button" class="hr-btn hr-btn-secondary" @click="reopenCase">Reopen</button>
        <button v-if="canCancel && !['cleared','closed','cancelled'].includes(activeCase.overall_status)" type="button" class="hr-btn hr-btn-danger" @click="cancelCase">Cancel</button>
      </div>

      <div class="stepper">
        <div
          v-for="s in activeCase.stages || []"
          :key="s.id"
          class="step"
          :class="stageClass(s)"
          @click="selectStage(s)"
        >
          <span class="dot"></span>
          <span class="name">{{ s.stage_name }}</span>
          <span class="st">{{ formatStatus(s.status) }}</span>
        </div>
      </div>

      <div class="clr-ws-tabs">
        <button v-for="w in workspaceTabs" :key="w.id" type="button" class="clr-tab" :class="{ on: wsTab === w.id }" @click="wsTab = w.id">{{ w.label }}</button>
      </div>

      <div v-if="wsTab !== 'stages' && currentActionableStage" class="clr-form-banner">
        <div>
          <strong>Clearance liability form is ready</strong>
          <p class="sub mb-0">Current stage: {{ currentActionableStage.stage_name }}. Open the form to complete and submit this section.</p>
        </div>
        <button type="button" class="hr-btn hr-btn-primary" @click="selectStage(currentActionableStage)">
          Open form
        </button>
      </div>

      <div v-if="wsTab === 'overview'" class="clr-grid-2">
        <div class="clr-card">
          <h4>Employee & exit</h4>
          <dl class="dl">
            <div><dt>Designation</dt><dd>{{ activeCase.designation || '—' }}</dd></div>
            <div><dt>Job group</dt><dd>{{ activeCase.job_group || '—' }}</dd></div>
            <div><dt>Station</dt><dd>{{ activeCase.work_station || '—' }}</dd></div>
            <div><dt>Notice date</dt><dd>{{ activeCase.notice_date || '—' }}</dd></div>
            <div><dt>Last working day</dt><dd>{{ activeCase.last_working_date || '—' }}</dd></div>
            <div><dt>Effective exit</dt><dd>{{ activeCase.effective_exit_date || '—' }}</dd></div>
            <div><dt>Reason</dt><dd>{{ activeCase.reason_for_exit || '—' }}</dd></div>
            <div><dt>Total liability</dt><dd class="money">{{ activeCase.liability_currency || 'KES' }} {{ formatMoney(activeCase.total_liability_amount) }}</dd></div>
            <div><dt>Final position</dt><dd>{{ activeCase.final_liability_position || '—' }}</dd></div>
            <div><dt>Certificate</dt><dd>{{ activeCase.certificate_number || 'Not issued' }}</dd></div>
          </dl>
        </div>
        <div class="clr-card">
          <h4>Completion checklist</h4>
          <ul class="checklist">
            <li v-for="s in activeCase.stages || []" :key="'ck-'+s.id">
              <i :class="checklistIcon(s)"></i>
              {{ s.stage_name }}
              <small>{{ formatStatus(s.status) }}</small>
            </li>
          </ul>
        </div>
      </div>

      <div v-if="wsTab === 'stages'" class="clr-card clr-liability-form-wrap">
        <div v-if="!selectedStage" class="clr-empty">
          <p>Select a stage from the progress strip above to open its liability form.</p>
          <button
            v-if="currentActionableStage"
            type="button"
            class="hr-btn hr-btn-primary"
            @click="selectStage(currentActionableStage)"
          >
            Open current stage form
          </button>
        </div>
        <div v-else class="stage-form liability-form">
          <!-- Official letterhead / officer header -->
          <header class="liab-letterhead">
            <div class="liab-crest-line">REPUBLIC OF KENYA · HOMABAY COUNTY</div>
            <h3>OFFICE OF THE GOVERNOR</h3>
            <h4>DIRECTORATE OF HUMAN RESOURCE MANAGEMENT AND DEVELOPMENT</h4>
            <p class="liab-addr">P. O. BOX 469-40300, HOMA BAY</p>
            <div class="liab-title">CLEARANCE LIABILITY FORM FOR EXITING OFFICER</div>
          </header>

          <section class="liab-officer-block">
            <div class="liab-row"><span class="liab-lbl">Name of the Officer</span><span class="liab-val">{{ activeCase.employee_name || '—' }}</span></div>
            <div class="liab-row"><span class="liab-lbl">Designation</span><span class="liab-val">{{ activeCase.designation || '—' }}</span></div>
            <div class="liab-row"><span class="liab-lbl">Personal Number</span><span class="liab-val">{{ activeCase.emp_no || '—' }}</span></div>
            <div class="liab-row"><span class="liab-lbl">Phone Number</span><span class="liab-val">{{ activeCase.phone || activeCase.mobile_number || '—' }}</span></div>
            <div class="liab-row"><span class="liab-lbl">Work Station</span><span class="liab-val">{{ activeCase.work_station || '—' }}</span></div>
            <div class="liab-row"><span class="liab-lbl">Address</span><span class="liab-val">{{ activeCase.physical_address || activeCase.address || '—' }}</span></div>
            <div class="liab-row"><span class="liab-lbl">Date</span><span class="liab-val">{{ activeCase.last_working_date || activeCase.created_at || '—' }}</span></div>
          </section>

          <!-- Prior stages: answers + documents (visible going forward) -->
          <section v-if="priorStagesSummary.length" class="liab-prior">
            <h5 class="liab-section-title">Previous stages (on record for this clearance)</h5>
            <div v-for="ps in priorStagesSummary" :key="'prior-'+ps.id" class="liab-prior-card">
              <div class="liab-prior-head">
                <strong>{{ ps.stage_name }}</strong>
                <span class="badge" :class="statusClass(ps.status)">{{ formatStatus(ps.status) }}</span>
              </div>
              <p v-if="ps.acted_by" class="sub">Signed/recorded by: {{ ps.acted_by }} · {{ ps.completed_at || '—' }}</p>
              <p v-if="ps.remarks" class="sub"><em>Remarks:</em> {{ ps.remarks }}</p>
              <ul v-if="ps.answerLines.length" class="liab-answer-list">
                <li v-for="(ln, i) in ps.answerLines" :key="i"><span>{{ ln.label }}:</span> {{ ln.value }}</li>
              </ul>
              <div v-if="ps.docs.length" class="liab-docs-mini">
                <span class="sub">Documents from this stage:</span>
                <button
                  v-for="d in ps.docs"
                  :key="d.id"
                  type="button"
                  class="liab-file-chip"
                  @click="viewDoc(d)"
                >
                  <i class="bx bx-file"></i> {{ d.document_type || 'File' }} — {{ d.original_name }}
                </button>
              </div>
            </div>
          </section>

          <!-- Current stage particular (IN CHARGE) -->
          <section class="liab-stage-block">
            <div class="liab-in-charge">
              <span class="liab-in-charge-label">IN CHARGE</span>
              <h5>{{ selectedStageOfficialTitle }}</h5>
              <p class="sub">Status: {{ formatStatus(selectedStage.status) }} · Role: {{ selectedStage.assigned_role || '—' }}</p>
            </div>

            <div class="form-grid liab-fields">
              <template v-for="field in stageFields(selectedStage)" :key="field.key">
                <label class="fld" :class="{ span2: field.span === 2 || field.type === 'textarea' || field.type === 'choice' || field.type === 'note' }">
                  <span>
                    {{ field.label }}
                    <em v-if="field.required" class="req">*</em>
                    <em v-if="field.hint" class="fld-hint"> — {{ field.hint }}</em>
                  </span>
                  <p v-if="field.type === 'note'" class="liab-note">{{ field.text }}</p>
                  <select v-else-if="field.type === 'yesno'" v-model="stageForm[field.key]" class="inp" :disabled="stageReadOnly">
                    <option value="">— Select —</option>
                    <option value="Yes">Yes</option>
                    <option value="No">No</option>
                  </select>
                  <select v-else-if="field.type === 'choice'" v-model="stageForm[field.key]" class="inp" :disabled="stageReadOnly">
                    <option value="">— Select —</option>
                    <option v-for="opt in field.options" :key="opt" :value="opt">{{ opt }}</option>
                  </select>
                  <input v-else-if="field.type === 'number'" v-model="stageForm[field.key]" type="number" step="0.01" class="inp" :disabled="stageReadOnly" placeholder="Indicate amount where necessary" />
                  <input v-else-if="field.type === 'date'" v-model="stageForm[field.key]" type="date" class="inp" :disabled="stageReadOnly" />
                  <textarea v-else-if="field.type === 'textarea'" v-model="stageForm[field.key]" class="inp ta" rows="3" :disabled="stageReadOnly" :placeholder="field.placeholder || ''"></textarea>
                  <input v-else v-model="stageForm[field.key]" type="text" class="inp" :disabled="stageReadOnly" :placeholder="field.placeholder || ''" />
                </label>
              </template>

              <label class="fld">
                <span>Officer name (signing this section)</span>
                <input v-model="stageForm.officer_name" type="text" class="inp" :disabled="stageReadOnly" placeholder="Full name" />
              </label>
              <label class="fld">
                <span>Date</span>
                <input v-model="stageForm.officer_date" type="date" class="inp" :disabled="stageReadOnly" />
              </label>

              <label class="fld span2">
                <span>Remarks / Particulars</span>
                <textarea v-model="stageRemarks" class="inp ta" rows="3" :disabled="stageReadOnly" placeholder="REMARKS.................................................................."></textarea>
              </label>
            </div>

            <!-- Stage document upload (visible to next stages) -->
            <div class="liab-attach-panel">
              <h6>
                Section documents
                <span v-if="stageUploadHint" class="req"> — {{ stageUploadHint }}</span>
              </h6>
              <p class="sub">
                Uploads are linked to this stage and remain visible to every later stage of this clearance.
                <strong>All liability must have documentary evidence.</strong>
              </p>

              <div v-if="currentStageDocs.length" class="liab-docs-list">
                <div v-for="d in currentStageDocs" :key="'cur-'+d.id" class="liab-doc-row">
                  <div>
                    <strong>{{ d.document_type }}</strong>
                    <span v-if="d.is_confidential == 1" class="badge warn">Confidential</span>
                    <div class="sub">{{ d.original_name }} · {{ d.uploaded_by }} · {{ d.created_at }}</div>
                  </div>
                  <button type="button" class="link" @click="viewDoc(d)">View</button>
                </div>
              </div>
              <div v-else class="sub" style="margin-bottom:8px">No documents attached to this section yet.</div>

              <div v-if="!stageReadOnly" class="upload-row liab-upload-row">
                <select v-model="stageUploadType" class="inp">
                  <option v-for="d in stageDocTypeOptions" :key="d" :value="d">{{ d }}</option>
                </select>
                <input type="file" @change="onStageFilePick" />
                <label class="chk"><input v-model="stageUploadConfidential" type="checkbox" /> Confidential</label>
                <button
                  type="button"
                  class="hr-btn hr-btn-primary"
                  :disabled="!stageUploadFile || stageUploading"
                  @click="uploadStageDoc"
                >
                  {{ stageUploading ? 'Uploading…' : 'Upload for this stage' }}
                </button>
              </div>
            </div>

            <div v-if="!stageReadOnly" class="btn-row">
              <button type="button" class="hr-btn hr-btn-secondary" @click="startStage">Start / open review</button>
              <button type="button" class="hr-btn hr-btn-success" @click="decideStage('cleared')">Submit cleared</button>
              <button type="button" class="hr-btn hr-btn-primary" @click="decideStage('cleared_with_liability')">Cleared with liability</button>
              <button type="button" class="hr-btn hr-btn-secondary" @click="decideStage('returned')">Return</button>
              <button type="button" class="hr-btn hr-btn-danger" @click="decideStage('not_cleared')">Not cleared</button>
              <button type="button" class="hr-btn hr-btn-outline-primary" @click="markNA">Not applicable…</button>
            </div>
            <div v-if="selectedStage.acted_by" class="approval-meta">
              Electronic signature: {{ selectedStage.acted_by }} · {{ selectedStage.completed_at || '—' }}
              <span v-if="selectedStage.approval_token"> · ref {{ selectedStage.approval_token }}</span>
            </div>
            <p class="liab-signature-line">SIGNATURE: …………………………………………… (captured electronically on submit)</p>
          </section>
        </div>
      </div>

      <div v-if="wsTab === 'liabilities'" class="clr-card">
        <div class="clr-card-head">
          <h4>Liabilities</h4>
          <button v-if="canLiab" type="button" class="hr-btn hr-btn-sm hr-btn-primary" @click="showLiabForm = !showLiabForm">Add liability</button>
        </div>
        <div v-if="showLiabForm" class="liab-form">
          <input v-model="liabForm.category" class="inp" placeholder="Category *" />
          <input v-model="liabForm.description" class="inp" placeholder="Description *" />
          <input v-model.number="liabForm.amount" type="number" class="inp" placeholder="Amount" />
          <input v-model="liabForm.currency" class="inp" placeholder="Currency" />
          <input v-model="liabForm.recovery_method" class="inp" placeholder="Recovery method" />
          <input v-model="liabForm.due_date" type="date" class="inp" />
          <button type="button" class="hr-btn hr-btn-primary" @click="addLiability">Save liability</button>
          <p class="sub">Attach evidence after saving (required when deciding “cleared with liability”).</p>
        </div>
        <table class="clr-table">
          <thead>
            <tr><th>Category</th><th>Description</th><th>Amount</th><th>Status</th><th>Due</th><th></th></tr>
          </thead>
          <tbody>
            <tr v-for="l in activeCase.liabilities || []" :key="l.id">
              <td>{{ l.category }}</td>
              <td>{{ l.description }}</td>
              <td>{{ l.currency }} {{ formatMoney(l.amount) }}</td>
              <td><span class="badge" :class="statusClass(l.recovery_status)">{{ formatStatus(l.recovery_status) }}</span></td>
              <td>{{ l.due_date || '—' }}</td>
              <td>
                <select v-if="canLiab" class="inp sm" @change="updateRecovery(l, $event.target.value)">
                  <option value="">Update recovery…</option>
                  <option value="under_review">Under review</option>
                  <option value="recovery_approved">Recovery approved</option>
                  <option value="partially_recovered">Partially recovered</option>
                  <option value="recovered">Recovered</option>
                  <option value="waived">Waived</option>
                  <option value="converted_to_debt">Converted to debt</option>
                  <option value="disputed">Disputed</option>
                </select>
              </td>
            </tr>
            <tr v-if="!(activeCase.liabilities || []).length"><td colspan="6" class="clr-empty">No liabilities recorded.</td></tr>
          </tbody>
        </table>
      </div>

      <div v-if="wsTab === 'documents'" class="clr-card">
        <div class="clr-card-head">
          <h4>Documents</h4>
        </div>
        <div class="upload-row">
          <select v-model="uploadType" class="inp">
            <option v-for="d in docTypes" :key="d" :value="d">{{ d }}</option>
          </select>
          <input type="file" @change="onFilePick" />
          <label class="chk"><input v-model="uploadConfidential" type="checkbox" /> Confidential</label>
          <button type="button" class="hr-btn hr-btn-primary" :disabled="!uploadFile" @click="uploadDoc">Upload</button>
        </div>
        <table class="clr-table">
          <thead>
            <tr><th>Type</th><th>Stage</th><th>File</th><th>Size</th><th>By</th><th>When</th><th></th></tr>
          </thead>
          <tbody>
            <tr v-for="a in activeCase.attachments || []" :key="a.id">
              <td>{{ a.document_type }} <span v-if="a.is_confidential == 1" class="badge warn">Confidential</span></td>
              <td>{{ attachmentStageName(a) }}</td>
              <td>{{ a.original_name }}</td>
              <td>{{ formatSize(a.file_size) }}</td>
              <td>{{ a.uploaded_by }}</td>
              <td>{{ a.created_at }}</td>
              <td><button type="button" class="link" @click="viewDoc(a)">View</button></td>
            </tr>
            <tr v-if="!(activeCase.attachments || []).length"><td colspan="7" class="clr-empty">No attachments.</td></tr>
          </tbody>
        </table>
      </div>

      <div v-if="wsTab === 'comments'" class="clr-card">
        <textarea v-model="commentText" class="inp ta" rows="3" placeholder="Add comment…"></textarea>
        <button type="button" class="hr-btn hr-btn-primary" style="margin-top:8px" @click="addComment">Post comment</button>
        <div v-for="c in activeCase.comments || []" :key="c.id" class="comment">
          <strong>{{ c.created_by }}</strong> <span class="sub">{{ c.created_at }}</span>
          <p>{{ c.comment_text }}</p>
        </div>
      </div>

      <div v-if="wsTab === 'audit'" class="clr-card">
        <div v-if="!(activeCase.audit || []).length" class="clr-empty">No audit events (or no permission).</div>
        <table v-else class="clr-table compact">
          <thead><tr><th>When</th><th>Action</th><th>Actor</th><th>Description</th></tr></thead>
          <tbody>
            <tr v-for="a in activeCase.audit" :key="a.id">
              <td>{{ a.created_at }}</td>
              <td>{{ a.action_type }}</td>
              <td>{{ a.actor }}</td>
              <td>{{ a.description }}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <!-- WIZARD: teleport to body so layout overflow/z-index can't hide it; avoid Bootstrap .modal -->
    <Teleport to="body">
      <div
        v-if="wizard.open"
        class="clr-overlay"
        role="dialog"
        aria-modal="true"
        aria-labelledby="clr-wiz-title"
        @click.self="wizard.open = false"
        @keydown.esc.prevent="wizard.open = false"
      >
        <div class="clr-dialog clr-dialog-wide" ref="wizardDialog">
          <div class="clr-dialog-head">
            <h3 id="clr-wiz-title">New clearance</h3>
            <button type="button" class="clr-icon-x" aria-label="Close" @click="wizard.open = false">×</button>
          </div>
          <div class="clr-wiz-steps">
            <span v-for="n in 5" :key="n" class="clr-ws" :class="{ on: wizard.step === n }">{{ n }}</span>
          </div>
          <div class="clr-dialog-body">
            <div v-if="wizard.step === 1">
              <label class="fld"><span>Search personal number (PF / National ID)</span>
                <div class="inline">
                  <input
                    ref="wizardEmpInput"
                    v-model="wizard.empNo"
                    class="inp"
                    placeholder="e.g. emp number"
                    @keyup.enter="searchEmp"
                  />
                  <button type="button" class="hr-btn hr-btn-primary" @click="searchEmp">Search</button>
                </div>
              </label>
              <div v-if="wizard.employee" class="emp-card">
                <strong>{{ wizard.employee.employee_name }}</strong>
                <div>{{ wizard.employee.emp_no }} · {{ wizard.employee.designation }}</div>
                <div>{{ wizard.employee.department_name }} · {{ wizard.employee.employment_status }}</div>
                <div v-if="wizard.employee.active_clearance" class="warn-text">
                  Active case exists: {{ wizard.employee.active_clearance.clearance_number }}
                  <label class="chk"><input v-model="wizard.exception_duplicate" type="checkbox" /> Allow exception</label>
                  <input v-if="wizard.exception_duplicate" v-model="wizard.exception_reason" class="inp" placeholder="Exception reason (required)" />
                </div>
              </div>
            </div>
            <div v-if="wizard.step === 2 && wizard.employee">
              <div class="form-grid">
                <label class="fld"><span>Exit type *</span>
                  <select v-model="wizard.exit_type" class="inp">
                    <option value="">Select</option>
                    <option v-for="e in exitTypes" :key="e.code" :value="e.code">{{ e.name }}</option>
                  </select>
                </label>
                <label class="fld"><span>Notice date</span><input v-model="wizard.notice_date" type="date" class="inp" /></label>
                <label class="fld"><span>Last working date *</span><input v-model="wizard.last_working_date" type="date" class="inp" /></label>
                <label class="fld"><span>Effective exit date</span><input v-model="wizard.effective_exit_date" type="date" class="inp" /></label>
                <label class="fld span2"><span>Reason for exit</span><textarea v-model="wizard.reason_for_exit" class="inp ta" rows="2"></textarea></label>
                <label class="fld"><span>Exit reference</span><input v-model="wizard.exit_reference_no" class="inp" /></label>
                <label class="fld"><span>Release letter ref</span><input v-model="wizard.release_letter_ref" class="inp" /></label>
                <label class="fld span2"><span>Remarks</span><input v-model="wizard.additional_remarks" class="inp" /></label>
              </div>
            </div>
            <div v-if="wizard.step === 3">
              <p class="sub">You can upload more documents after the case is created. Optional initial docs:</p>
              <p class="sub">Resignation / exit letter, release letter, handover report…</p>
            </div>
            <div v-if="wizard.step === 4">
              <p>Preview route for <strong>{{ formatExit(wizard.exit_type) }}</strong> (default stages including CPSB where applicable):</p>
              <ol class="route-list">
                <li v-for="s in configStages.filter(x => x.is_active == 1 || x.is_active === '1' || x.is_active === true)" :key="s.stage_code">
                  {{ s.stage_name }} <small>({{ s.stage_group }} · SLA {{ s.sla_days }}d)</small>
                </li>
              </ol>
              <p v-if="!configStages.length" class="sub">Stage list will load from configuration when available.</p>
            </div>
            <div v-if="wizard.step === 5">
              <p>Confirm initiation for <strong>{{ wizard.employee?.employee_name }}</strong> ({{ wizard.employee?.emp_no }}).</p>
              <p>Exit: {{ formatExit(wizard.exit_type) }} · Last day: {{ wizard.last_working_date }}</p>
            </div>
            <p v-if="wizard.error" class="error">{{ wizard.error }}</p>
          </div>
          <div class="clr-dialog-foot">
            <button type="button" class="hr-btn hr-btn-secondary" :disabled="wizard.step === 1" @click="wizard.step--">Back</button>
            <button v-if="wizard.step < 5" type="button" class="hr-btn hr-btn-primary" @click="nextWizard">Next</button>
            <button v-else type="button" class="hr-btn hr-btn-success" :disabled="wizard.saving" @click="createCase">
              {{ wizard.saving ? 'Creating…' : 'Confirm & create' }}
            </button>
          </div>
        </div>
      </div>
    </Teleport>
  </HrPageLayout>
</template>

<script>
/* eslint-disable */
import HrPageLayout from "@/components/hrActions/HrPageLayout.vue";
import { authUrl, execute, executeDownload, getRight, toast } from "@/api";
import { downloadCsv, downloadCountyTablePdf } from "@/utils/countyReportExport";
import { jsPDF } from "jspdf";

export default {
  name: "EmployeeClearance",
  components: { HrPageLayout },
  data() {
    return {
      view: "dashboard",
      wsTab: "overview",
      loading: false,
      stats: {},
      recent: [],
      pipeline: [],
      stuckCases: [],
      viewerName: "",
      canAlert: false,
      alertingId: null,
      cases: [],
      tasks: [],
      exitTypes: [],
      configStages: [],
      filters: { search: "", status: "", exitType: "", overdueOnly: false },
      statusOptions: [
        "draft", "awaiting_employee", "in_departmental", "in_specialist", "in_cpsb",
        "awaiting_final_hr", "returned", "on_hold", "cleared", "closed", "cancelled"
      ],
      activeCase: null,
      selectedStage: null,
      stageForm: {},
      stageRemarks: "",
      showLiabForm: false,
      liabForm: { category: "", description: "", amount: 0, currency: "KES", recovery_method: "", due_date: "" },
      uploadType: "Resignation or exit letter",
      uploadFile: null,
      uploadConfidential: false,
      stageUploadType: "Supporting document",
      stageUploadFile: null,
      stageUploadConfidential: false,
      stageUploading: false,
      commentText: "",
      reportType: "status",
      reportRows: [],
      reportHeaders: [],
      reportLoading: false,
      wizard: {
        open: false, step: 1, empNo: "", employee: null, exit_type: "",
        notice_date: "", last_working_date: "", effective_exit_date: "",
        reason_for_exit: "", exit_reference_no: "", release_letter_ref: "",
        additional_remarks: "", exception_duplicate: false, exception_reason: "",
        saving: false, error: ""
      },
      docTypes: [
        "Resignation or exit letter", "Release letter", "Handover report",
        "Exit interview report", "Departmental clearance report",
        "Promotion / redesignation / confirmation letter", "Asset return evidence",
        "Imprest surrender evidence", "Housing clearance evidence",
        "Payroll recovery documentation", "Supporting document"
      ],
      metricCards: [
        { key: "active", label: "Active cases" },
        { key: "awaiting_employee", label: "Awaiting employee" },
        { key: "in_departmental", label: "Departmental" },
        { key: "in_specialist", label: "Specialist" },
        { key: "in_cpsb", label: "CPSB" },
        { key: "awaiting_final_hr", label: "Final HR" },
        { key: "overdue", label: "Overdue", tone: "bad" },
        { key: "cleared", label: "Cleared", tone: "ok" },
        { key: "open_liabilities", label: "Open liabilities" },
        { key: "liability_value", label: "Liability value", money: true },
        { key: "avg_completion_days", label: "Avg days to clear" },
      ]
    };
  },
  computed: {
    tabs() {
      const t = [
        { id: "dashboard", label: "Dashboard", icon: "bx bx-grid-alt" },
        { id: "list", label: "Cases", icon: "bx bx-list-ul" },
        { id: "tasks", label: "My tasks", icon: "bx bx-task" },
        { id: "reports", label: "Reports", icon: "bx bx-bar-chart" }
      ];
      if (this.canConfigure) t.push({ id: "config", label: "Configuration", icon: "bx bx-cog" });
      return t;
    },
    workspaceTabs() {
      return [
        { id: "overview", label: "Overview" },
        { id: "stages", label: "Stages" },
        { id: "liabilities", label: "Liabilities" },
        { id: "documents", label: "Documents" },
        { id: "comments", label: "Comments" },
        { id: "audit", label: "Audit" }
      ];
    },
    canCreate() {
      return getRight([
        "CLEARANCE CREATE",
        "CLEARANCE MANAGE",
        "CLEARANCE UPDATE",
        "CLEARANCE SUBMIT",
        "CLEARANCE VIEW",
        "USER MANAGEMENT",
        "TERMINATION",
        "SEPARATION"
      ]);
    },
    canSubmit() { return getRight(["CLEARANCE SUBMIT", "CLEARANCE CREATE", "CLEARANCE MANAGE"]); },
    canApprove() {
      return getRight([
        "CLEARANCE APPROVE", "CLEARANCE REVIEW", "CLEARANCE MANAGE",
        "CLEARANCE STAGE DEPT ACCOUNTANT", "CLEARANCE STAGE DEPT PROCUREMENT", "CLEARANCE STAGE DEPT HR",
        "CLEARANCE STAGE HEAD OF SECTION", "CLEARANCE STAGE ACCOUNTING OFFICER",
        "CLEARANCE STAGE CO HOUSING", "CLEARANCE STAGE DIRECTOR SCM", "CLEARANCE STAGE CO FINANCE",
        "CLEARANCE STAGE HEAD OF ACCOUNTS", "CLEARANCE STAGE PSB CEO", "CLEARANCE STAGE PSB CHAIR",
        "CLEARANCE STAGE PAYROLL MANAGER"
      ]);
    },
    canFinal() { return getRight(["CLEARANCE FINAL VERIFY", "CLEARANCE MANAGE", "DIRECTOR HR"]); },
    canReopen() { return getRight(["CLEARANCE REOPEN", "CLEARANCE MANAGE"]); },
    canCancel() { return getRight(["CLEARANCE CANCEL", "CLEARANCE MANAGE"]); },
    canConfigure() { return getRight(["CLEARANCE CONFIGURE", "CLEARANCE MANAGE", "USER MANAGEMENT"]); },
    canLiab() { return getRight(["CLEARANCE MANAGE LIABILITIES", "CLEARANCE APPROVE", "CLEARANCE MANAGE"]); },
    stageReadOnly() {
      if (!this.activeCase || !this.selectedStage) return true;
      if (["cleared", "closed", "cancelled"].includes(String(this.activeCase.overall_status).toLowerCase())) return true;
      const st = String(this.selectedStage.status || "").toLowerCase();
      return ["cleared", "cleared_with_liability", "not_cleared", "not_applicable"].includes(st) || !this.canApprove;
    },
    currentActionableStage() {
      if (!this.activeCase?.stages?.length) return null;
      const stages = this.activeCase.stages;
      return (
        stages.find((s) =>
          ["pending", "in_review", "returned"].includes(String(s.status || "").toLowerCase())
        ) ||
        stages.find(
          (s) =>
            this.activeCase.current_stage_code &&
            s.stage_code === this.activeCase.current_stage_code
        ) ||
        stages[0] ||
        null
      );
    },
    selectedStageOfficialTitle() {
      if (!this.selectedStage) return "";
      const map = {
        dept_accountant: "DEPARTMENTAL ACCOUNTANT",
        dept_procurement: "DEPARTMENTAL PROCUREMENT",
        dept_hrm: "DEPARTMENTAL (HRM)",
        head_of_section: "HEAD OF SECTION (DIRECTOR IN CHARGE)",
        accounting_officer: "ACCOUNTING OFFICER (DEPARTMENTAL CHIEF OFFICER)",
        chief_officer_housing: "CHIEF OFFICER HOUSING",
        director_scm: "COUNTY DIRECTOR SUPPLY CHAIN MANAGEMENT OR DIRECTOR ICT",
        chief_officer_finance: "CHIEF OFFICER FINANCE",
        head_of_accounts: "HEAD OF ACCOUNTS – FINANCE",
        cpsb_ceo: "CEO / SECRETARY COUNTY PUBLIC SERVICE BOARD",
        cpsb_chair: "CHAIRPERSON COUNTY PUBLIC SERVICE BOARD",
        payroll_manager: "COUNTY PAYROLL MANAGER",
        county_director_hrm: "VERIFICATION BY COUNTY DIRECTOR OF HUMAN RESOURCE MANAGEMENT"
      };
      return map[this.selectedStage.stage_code] || String(this.selectedStage.stage_name || "").toUpperCase();
    },
    currentStageDocs() {
      if (!this.activeCase || !this.selectedStage) return [];
      const sid = Number(this.selectedStage.id);
      return (this.activeCase.attachments || []).filter((a) => Number(a.stage_id) === sid);
    },
    priorStagesSummary() {
      if (!this.activeCase || !this.selectedStage) return [];
      const stages = this.activeCase.stages || [];
      const curSort = Number(this.selectedStage.sort_order || 0);
      const done = ["cleared", "cleared_with_liability", "not_applicable", "not_cleared"];
      return stages
        .filter((s) => Number(s.sort_order) < curSort || (Number(s.sort_order) === curSort && Number(s.id) < Number(this.selectedStage.id)))
        .filter((s) => done.includes(String(s.status || "").toLowerCase()) || (s.response && Object.keys(s.response).length))
        .map((s) => {
          const fields = this.stageFields(s);
          const resp = s.response || {};
          const answerLines = fields
            .filter((f) => f.type !== "note" && resp[f.key] !== undefined && resp[f.key] !== null && String(resp[f.key]).trim() !== "")
            .map((f) => ({ label: f.label, value: String(resp[f.key]) }));
          if (resp.officer_name) answerLines.push({ label: "Officer name", value: resp.officer_name });
          if (resp.officer_date) answerLines.push({ label: "Date", value: resp.officer_date });
          const docs = (this.activeCase.attachments || []).filter((a) => Number(a.stage_id) === Number(s.id));
          return {
            id: s.id,
            stage_name: s.stage_name,
            status: s.status,
            acted_by: s.acted_by,
            completed_at: s.completed_at,
            remarks: s.remarks,
            answerLines,
            docs
          };
        });
    },
    stageUploadHint() {
      if (!this.selectedStage) return "";
      const schema = this.selectedStage.form_schema || this.selectedStage.stage_code;
      const hints = {
        dept_hrm: "Please attach Departmental Clearance Report",
        procurement: "Attach handover report if Yes",
        accounting_officer: "Attach promotion / redesignation / confirmation letter if Yes",
        cpsb_ceo: "Attach release letter and exit interview report where indicated Yes",
        housing: "Attach housing clearance evidence if occupying government house",
        scm_ict: "Attach asset return evidence where applicable",
        payroll: "All liability must have documentary evidence",
        final_hrm: "Review all prior documents before final verification"
      };
      return hints[schema] || "Upload supporting evidence for this section";
    },
    stageDocTypeOptions() {
      const schema = this.selectedStage?.form_schema || this.selectedStage?.stage_code || "";
      const preferred = {
        dept_hrm: ["Departmental clearance report", "Supporting document"],
        procurement: ["Handover report", "Asset return evidence", "Supporting document"],
        accounting_officer: ["Promotion / redesignation / confirmation letter", "Supporting document"],
        cpsb_ceo: ["Release letter", "Exit interview report", "Supporting document"],
        housing: ["Housing clearance evidence", "Supporting document"],
        scm_ict: ["Asset return evidence", "Handover report", "Supporting document"],
        payroll: ["Payroll recovery documentation", "Supporting document"],
        dept_accounts: ["Imprest surrender evidence", "Supporting document"],
        finance: ["Imprest surrender evidence", "Supporting document"],
        final_hrm: ["Supporting document"]
      };
      const list = preferred[schema] || this.docTypes;
      // union keep unique
      const set = new Set([...list, ...this.docTypes]);
      return Array.from(set);
    }
  },
  mounted() {
    this.refreshAll();
  },
  methods: {
    api(fn, fields = {}) {
      const fd = new FormData();
      fd.append("function", fn);
      Object.keys(fields).forEach((k) => {
        const v = fields[k];
        if (v === undefined || v === null) return;
        fd.append(k, typeof v === "object" && !(v instanceof File) ? JSON.stringify(v) : v);
      });
      return execute(fd, authUrl);
    },
    formatMetric(v, money) {
      if (money) return `KES ${this.formatMoney(v)}`;
      if (v == null) return "0";
      if (typeof v === "number" && !Number.isInteger(v)) return v.toFixed(1);
      return v;
    },
    formatMoney(v) {
      const n = Number(v || 0);
      return n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    },
    formatSize(b) {
      const n = Number(b || 0);
      if (n < 1024) return `${n} B`;
      if (n < 1048576) return `${(n / 1024).toFixed(1)} KB`;
      return `${(n / 1048576).toFixed(1)} MB`;
    },
    formatStatus(s) {
      return String(s || "—").replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
    },
    formatExit(s) {
      return this.formatStatus(s);
    },
    attachmentStageName(a) {
      if (a.stage_name) return a.stage_name;
      if (!a.stage_id || !this.activeCase?.stages) return "—";
      const st = this.activeCase.stages.find((s) => Number(s.id) === Number(a.stage_id));
      return st?.stage_name || "—";
    },
    statusClass(s) {
      const v = String(s || "").toLowerCase();
      if (v.includes("clear") || v === "recovered" || v === "ok") return "ok";
      if (v.includes("cancel") || v.includes("not_cleared") || v.includes("reject")) return "bad";
      if (v.includes("return") || v.includes("hold") || v.includes("pending") || v.includes("await")) return "warn";
      return "";
    },
    isOverdue(d) {
      if (!d) return false;
      return new Date(d).getTime() < Date.now();
    },
    stageClass(s) {
      const st = String(s.status || "").toLowerCase();
      return {
        done: ["cleared", "cleared_with_liability", "not_applicable"].includes(st),
        active: ["pending", "in_review"].includes(st),
        returned: st === "returned",
        overdue: this.isOverdue(s.due_at) && ["pending", "in_review", "returned"].includes(st),
        selected: this.selectedStage && this.selectedStage.id === s.id
      };
    },
    checklistIcon(s) {
      const st = String(s.status || "").toLowerCase();
      if (["cleared", "cleared_with_liability"].includes(st)) return "bx bx-check-circle ok-i";
      if (st === "not_applicable") return "bx bx-minus-circle";
      if (st === "returned") return "bx bx-error-circle warn-i";
      return "bx bx-circle";
    },
    switchView(id) {
      this.view = id;
      if (id === "list") this.loadCases();
      if (id === "tasks") this.loadTasks();
      if (id === "dashboard") this.loadDashboard();
      if (id === "config") this.loadConfig();
      if (id === "reports") this.loadReport();
    },
    async refreshAll() {
      await Promise.all([this.loadDashboard(), this.loadConfig(), this.loadTasks()]);
    },
    async loadDashboard() {
      try {
        const res = await this.api("clrGetDashboard");
        if (res.data?.success) {
          this.stats = res.data.data?.stats || {};
          this.recent = res.data.data?.recent || [];
          this.pipeline = res.data.data?.pipeline || [];
          this.stuckCases = res.data.data?.stuckCases || [];
          this.viewerName = res.data.data?.viewer?.name || "";
          this.canAlert = !!res.data.data?.canAlert || this.canFinal;
        } else {
          toast.error(res.data?.message || "Failed to load clearance dashboard");
        }
      } catch (_) {
        toast.error("Failed to load clearance dashboard");
      }
    },
    async sendDelayAlert(row) {
      if (!row?.id) return;
      const note = window.prompt(
        "Delay alert message (optional — leave blank for default):",
        ""
      );
      if (note === null) return;
      this.alertingId = row.id;
      try {
        const res = await this.api("clrSendDelayAlert", {
          caseId: row.id,
          stageId: row.stage_id || "",
          message: note || ""
        });
        if (res.data?.success) {
          toast.success(res.data.message || "Delay alert sent");
          await this.loadDashboard();
        } else {
          toast.error(res.data?.message || "Could not send alert");
        }
      } catch (e) {
        toast.error(e?.message || "Could not send alert");
      } finally {
        this.alertingId = null;
      }
    },
    async loadConfig() {
      try {
        const res = await this.api("clrGetConfig");
        if (res.data?.success) {
          this.configStages = res.data.data?.stages || [];
          this.exitTypes = res.data.data?.exitTypes || [];
        }
      } catch (_) {}
    },
    async loadCases() {
      this.loading = true;
      try {
        const res = await this.api("clrListCases", {
          search: this.filters.search,
          status: this.filters.status,
          exitType: this.filters.exitType,
          overdueOnly: this.filters.overdueOnly ? "1" : "0"
        });
        this.cases = res.data?.success ? res.data.data?.cases || [] : [];
      } catch (_) {
        this.cases = [];
      } finally {
        this.loading = false;
      }
    },
    async loadTasks() {
      try {
        const res = await this.api("clrGetMyTasks");
        this.tasks = res.data?.success ? res.data.data?.tasks || [] : [];
        if (res.data?.data?.viewer?.name) this.viewerName = res.data.data.viewer.name;
      } catch (_) {
        this.tasks = [];
      }
    },
    async openCase(id, stageId = null) {
      this.loading = true;
      try {
        const res = await this.api("clrGetCase", { id });
        if (!res.data?.success) {
          toast.error(res.data?.message || "Failed to load case");
          return;
        }
        this.activeCase = res.data.data.case;
        this.view = "case";

        const stages = this.activeCase.stages || [];
        let target = null;
        if (stageId) {
          target = stages.find((s) => Number(s.id) === Number(stageId)) || null;
        }
        // Prefer the currently active / first actionable stage so the liability form is visible
        if (!target) {
          target =
            stages.find((s) =>
              ["pending", "in_review", "returned"].includes(String(s.status || "").toLowerCase())
            ) ||
            stages.find(
              (s) =>
                this.activeCase.current_stage_code &&
                s.stage_code === this.activeCase.current_stage_code
            ) ||
            stages[0] ||
            null;
        }

        if (target) {
          this.selectStage(target);
        } else {
          this.selectedStage = null;
          this.wsTab = "overview";
        }

        this.$nextTick(() => {
          const formEl = document.querySelector(".liability-form") || document.querySelector(".clr-liability-form-wrap");
          const el = formEl || document.querySelector(".clr-case-top");
          if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
        });
      } finally {
        this.loading = false;
      }
    },
    selectStage(s) {
      this.selectedStage = s;
      const resp = { ...(s.response || {}) };
      if (!resp.officer_name && this.viewerName) resp.officer_name = this.viewerName;
      if (!resp.officer_date) {
        resp.officer_date = new Date().toISOString().slice(0, 10);
      }
      this.stageForm = resp;
      this.stageRemarks = s.remarks || "";
      this.stageUploadFile = null;
      // Prefer schema-related default doc type
      const opts = this.stageDocTypeOptions;
      this.stageUploadType = opts[0] || "Supporting document";
      this.stageUploadConfidential = String(this.stageUploadType || "").toLowerCase().includes("exit interview");
      this.wsTab = "stages";
    },
    stageFields(stage) {
      const schema = stage.form_schema || stage.stage_code || "generic";
      const map = {
        // DEPARTMENTAL ACCOUNTANT
        dept_accounts: [
          { key: "pending_claim", label: "Pending claim", type: "text", placeholder: "Indicate amount / status where necessary", span: 2 },
          { key: "unpaid_payment_via_voucher", label: "Unpaid payment via voucher", type: "text", placeholder: "Indicate amount where necessary", span: 2 },
          { key: "unsurrendered_imprest", label: "Unsurrendered imprest", type: "text", placeholder: "Indicate amount where necessary", span: 2 }
        ],
        // DEPARTMENTAL PROCUREMENT
        procurement: [
          {
            key: "handover_report_office_equipment",
            label: "Handover report (office equipment e.g. laptops, phones etc.)",
            type: "yesno",
            required: true,
            span: 2
          }
        ],
        // DEPARTMENTAL (HRM)
        dept_hrm: [
          {
            key: "note_dept_clearance",
            type: "note",
            text: "Please attach Departmental Clearance Report before clearing this section."
          }
        ],
        // HEAD OF SECTION
        handover: [
          { key: "hos_particulars", label: "Particulars and remarks for head of section", type: "textarea", span: 2, placeholder: "State particulars of exiting officer's section status" }
        ],
        // ACCOUNTING OFFICER
        accounting_officer: [
          {
            key: "uneffected_promotion_redesignation",
            label: "Uneffected promotions / redesignation (attach promotion / redesignation / confirmation letter if any)",
            type: "yesno",
            required: true,
            span: 2
          }
        ],
        // CO HOUSING
        housing: [
          { key: "occupying_government_house", label: "Is the staff occupying Government House?", type: "yesno", required: true },
          { key: "staff_housing_status", label: "Staff housing status", type: "text", span: 2, placeholder: "Describe housing status" }
        ],
        // SCM / ICT
        scm_ict: [
          {
            key: "handover_status_equipment",
            label: "Handover status — office equipment (e.g. laptops, printers, phones etc.)",
            type: "textarea",
            required: true,
            span: 2,
            placeholder: "List equipment and handover status"
          }
        ],
        // CO FINANCE
        finance: [
          { key: "pending_claim", label: "Pending claim", type: "text", placeholder: "Amount / status where necessary", span: 2 },
          { key: "unsurrendered_imprest", label: "Unsurrendered imprest", type: "text", placeholder: "Amount where necessary", span: 2 },
          { key: "unpaid_voucher_request", label: "Unpaid voucher request", type: "text", placeholder: "Amount / status where necessary", span: 2 }
        ],
        // HEAD OF ACCOUNTS
        accounts: [
          { key: "payment_status", label: "Payment status (if any)", type: "textarea", span: 2, placeholder: "State payment status if any" }
        ],
        // PAYROLL
        payroll: [
          {
            key: "liability_recovery",
            label: "Liability recovery (overpayment / underpayment / arrears / leave etc.) — state amount if any",
            type: "textarea",
            span: 2,
            placeholder: "State amount if any. All liability must have documentary evidence."
          }
        ],
        // CEO / SECRETARY CPSB
        cpsb_ceo: [
          { key: "terms_of_engagement", label: "i. Terms of engagement", type: "choice", options: ["P&P", "Contract"], required: true },
          { key: "release_letter_submitted", label: "ii. Submitted release letter from Department", type: "yesno", required: true },
          { key: "released_by_cpsb", label: "iii. Released by CPSB (if yes include date of release)", type: "yesno", required: true },
          { key: "date_of_release", label: "Date of release", type: "date" },
          { key: "exit_interview_conducted", label: "iv. Exit interview conducted (if yes attach exit interview report — submit confidentially to Directorate of HRM&D)", type: "yesno", required: true, span: 2 }
        ],
        // PSB CHAIR
        cpsb_chair: [
          { key: "chair_particulars", label: "Particulars and remarks", type: "textarea", span: 2, placeholder: "Chairperson remarks on exiting officer" }
        ],
        // DIRECTOR HR final
        final_hrm: [
          { key: "verified_by", label: "Verified by", type: "text", required: true, span: 2 },
          { key: "verification_notes", label: "Verification remarks", type: "textarea", span: 2 },
          {
            key: "note_stamp",
            type: "note",
            text: "OFFICIAL STAMP: electronic verification on submit constitutes directorate stamp. Review prior stages and attached documents before final clearance."
          }
        ],
        generic: [
          { key: "particulars", label: "Particulars and remarks", type: "textarea", span: 2 }
        ]
      };
      return map[schema] || map.generic;
    },
    onStageFilePick(e) {
      this.stageUploadFile = e.target.files?.[0] || null;
    },
    async uploadStageDoc() {
      if (!this.stageUploadFile || !this.activeCase || !this.selectedStage) {
        return toast.error("Select a file and open a stage first");
      }
      this.stageUploading = true;
      try {
        const fd = new FormData();
        fd.append("function", "clrUploadAttachment");
        fd.append("caseId", String(this.activeCase.id));
        fd.append("stageId", String(this.selectedStage.id));
        fd.append("document_type", this.stageUploadType);
        const conf =
          this.stageUploadConfidential ||
          String(this.stageUploadType || "").toLowerCase().includes("exit interview")
            ? "1"
            : "0";
        fd.append("is_confidential", conf);
        fd.append("file", this.stageUploadFile);
        const res = await execute(fd, authUrl);
        if (res.data?.success) {
          toast.success("Document uploaded — will be visible on later stages");
          const sid = this.selectedStage.id;
          const keepForm = { ...this.stageForm };
          const keepRemarks = this.stageRemarks;
          this.stageUploadFile = null;
          await this.openCase(this.activeCase.id, sid);
          this.stageForm = { ...keepForm };
          this.stageRemarks = keepRemarks;
          this.wsTab = "stages";
        } else {
          toast.error(res.data?.message || "Upload failed");
        }
      } finally {
        this.stageUploading = false;
      }
    },
    validateStageFormBeforeDecision(decision) {
      if (["returned", "not_cleared"].includes(decision)) return true;
      const fields = this.stageFields(this.selectedStage);
      for (const f of fields) {
        if (f.type === "note") continue;
        if (f.required && (!this.stageForm[f.key] || String(this.stageForm[f.key]).trim() === "")) {
          toast.error(`${f.label} is required`);
          return false;
        }
      }
      if (!this.stageForm.officer_name || !String(this.stageForm.officer_name).trim()) {
        toast.error("Officer name is required for this section");
        return false;
      }
      if (!this.stageForm.officer_date) {
        toast.error("Date is required for this section");
        return false;
      }

      const schema = this.selectedStage.form_schema || this.selectedStage.stage_code;
      const stageDocs = this.currentStageDocs;
      const hasType = (needle) =>
        stageDocs.some((d) => String(d.document_type || "").toLowerCase().includes(needle));

      if (schema === "dept_hrm" && stageDocs.length < 1) {
        toast.error("Please attach Departmental Clearance Report for this section");
        return false;
      }
      if (schema === "procurement" && this.stageForm.handover_report_office_equipment === "Yes" && stageDocs.length < 1) {
        toast.error("Attach the handover report for office equipment");
        return false;
      }
      if (
        schema === "accounting_officer" &&
        this.stageForm.uneffected_promotion_redesignation === "Yes" &&
        stageDocs.length < 1
      ) {
        toast.error("Attach promotion / redesignation / confirmation letter");
        return false;
      }
      if (schema === "cpsb_ceo") {
        if (this.stageForm.release_letter_submitted === "Yes" && !hasType("release")) {
          // Allow any stage doc if type naming varies
          if (stageDocs.length < 1 && !this.attachmentExistsOnCase("release")) {
            toast.error("Attach the release letter from department");
            return false;
          }
        }
        if (this.stageForm.released_by_cpsb === "Yes" && !this.stageForm.date_of_release) {
          toast.error("Date of release is required when Released by CPSB is Yes");
          return false;
        }
        if (this.stageForm.exit_interview_conducted === "Yes") {
          const hasExit =
            hasType("exit interview") || this.attachmentExistsOnCase("exit interview");
          if (!hasExit) {
            toast.error("Attach confidential exit interview report");
            return false;
          }
        }
      }
      if (schema === "housing" && this.stageForm.occupying_government_house === "Yes" && stageDocs.length < 1) {
        toast.error("Attach housing clearance evidence if staff occupies a government house");
        return false;
      }
      if (decision === "cleared_with_liability" && stageDocs.length < 1) {
        // liability evidence also enforced server-side via liability table
      }
      return true;
    },
    attachmentExistsOnCase(needle) {
      const n = String(needle).toLowerCase();
      return (this.activeCase?.attachments || []).some((d) =>
        String(d.document_type || "").toLowerCase().includes(n)
      );
    },
    async startStage() {
      if (!this.selectedStage) return;
      const res = await this.api("clrStartStage", { stageId: this.selectedStage.id });
      if (res.data?.success) {
        toast.success("Stage opened for review — complete the liability form section");
        await this.openCase(this.activeCase.id, this.selectedStage.id);
      } else toast.error(res.data?.message || "Failed");
    },
    async decideStage(decision) {
      if (!this.selectedStage) return;
      if (decision === "returned" && String(this.stageRemarks).trim().length < 3) {
        return toast.error("Return reason is required");
      }
      if (!this.validateStageFormBeforeDecision(decision)) return;
      const res = await this.api("clrSubmitStageDecision", {
        stageId: this.selectedStage.id,
        decision,
        remarks: this.stageRemarks,
        response_json: JSON.stringify(this.stageForm)
      });
      if (res.data?.success) {
        toast.success(res.data.message || "Decision saved — advanced to next stage");
        this.activeCase = res.data.data.case;
        // Jump to next active stage if any
        const next = (this.activeCase.stages || []).find((s) =>
          ["pending", "in_review", "returned"].includes(String(s.status || "").toLowerCase())
        );
        if (next) this.selectStage(next);
        else if (this.selectedStage) {
          const sid = this.selectedStage.id;
          this.selectedStage = (this.activeCase.stages || []).find((s) => Number(s.id) === Number(sid)) || null;
          if (this.selectedStage) this.selectStage(this.selectedStage);
        }
      } else toast.error(res.data?.message || "Decision failed");
    },
    async markNA() {
      const reason = window.prompt("Reason for Not Applicable (required):");
      if (!reason || reason.trim().length < 3) return;
      const res = await this.api("clrMarkStageNA", { stageId: this.selectedStage.id, reason });
      if (res.data?.success) {
        toast.success("Marked N/A");
        this.activeCase = res.data.data.case;
      } else toast.error(res.data?.message || "Failed");
    },
    async addLiability() {
      if (!this.liabForm.category || !this.liabForm.description) {
        return toast.error("Category and description required");
      }
      const res = await this.api("clrAddLiability", {
        caseId: this.activeCase.id,
        stageId: this.selectedStage?.id || "",
        ...this.liabForm
      });
      if (res.data?.success) {
        toast.success("Liability recorded — attach evidence");
        this.showLiabForm = false;
        this.liabForm = { category: "", description: "", amount: 0, currency: "KES", recovery_method: "", due_date: "" };
        await this.openCase(this.activeCase.id);
        this.wsTab = "liabilities";
      } else toast.error(res.data?.message || "Failed");
    },
    async updateRecovery(l, status) {
      if (!status) return;
      const res = await this.api("clrUpdateLiabilityRecovery", {
        liabilityId: l.id,
        recovery_status: status,
        amount: l.amount
      });
      if (res.data?.success) {
        toast.success("Recovery updated");
        await this.openCase(this.activeCase.id);
        this.wsTab = "liabilities";
      } else toast.error(res.data?.message || "Failed");
    },
    onFilePick(e) {
      this.uploadFile = e.target.files?.[0] || null;
    },
    async uploadDoc() {
      if (!this.uploadFile || !this.activeCase) return;
      const fd = new FormData();
      fd.append("function", "clrUploadAttachment");
      fd.append("caseId", String(this.activeCase.id));
      fd.append("document_type", this.uploadType);
      fd.append("is_confidential", this.uploadConfidential || this.uploadType.toLowerCase().includes("exit interview") ? "1" : "0");
      if (this.selectedStage) fd.append("stageId", String(this.selectedStage.id));
      fd.append("file", this.uploadFile);
      const res = await execute(fd, authUrl);
      if (res.data?.success) {
        toast.success("Uploaded");
        this.uploadFile = null;
        await this.openCase(this.activeCase.id);
        this.wsTab = "documents";
      } else toast.error(res.data?.message || "Upload failed");
    },
    async viewDoc(a) {
      try {
        const fd = new FormData();
        fd.append("function", "clrDownloadAttachment");
        fd.append("id", String(a.id));
        const res = await executeDownload(fd, authUrl);
        const blob = res.data;
        const ct = String(res.headers?.["content-type"] || "");
        if (ct.includes("json")) {
          const t = await blob.text();
          const j = JSON.parse(t);
          return toast.error(j.message || "Cannot open file");
        }
        const url = URL.createObjectURL(blob);
        window.open(url, "_blank");
        setTimeout(() => URL.revokeObjectURL(url), 120000);
      } catch (e) {
        toast.error(e?.message || "Could not open file");
      }
    },
    async addComment() {
      if (!this.commentText.trim()) return;
      const res = await this.api("clrAddComment", { caseId: this.activeCase.id, comment: this.commentText });
      if (res.data?.success) {
        this.commentText = "";
        await this.openCase(this.activeCase.id);
        this.wsTab = "comments";
      } else toast.error(res.data?.message || "Failed");
    },
    async submitCase() {
      const res = await this.api("clrSubmitCase", { id: this.activeCase.id });
      if (res.data?.success) {
        toast.success("Submitted into clearance workflow");
        this.activeCase = res.data.data.case;
      } else toast.error(res.data?.message || "Submit failed");
    },
    async runFinal() {
      const remarks = window.prompt("Final verification remarks:");
      if (remarks == null) return;
      const allow = window.confirm("If open liabilities exist, OK to allow with recovery plan, Cancel to block.") ? "1" : "0";
      const res = await this.api("clrFinalVerify", {
        id: this.activeCase.id,
        decision: "cleared",
        verification_remarks: remarks,
        allow_with_recovery_plan: allow
      });
      if (res.data?.success) {
        toast.success(res.data.message || "Final verification done");
        this.activeCase = res.data.data.case;
      } else toast.error(res.data?.message || "Final verification blocked");
    },
    async reopenCase() {
      const reason = window.prompt("Reopen reason (required):");
      if (!reason || reason.trim().length < 5) return;
      const res = await this.api("clrReopenCase", { id: this.activeCase.id, reason });
      if (res.data?.success) {
        toast.success("Case reopened");
        this.activeCase = res.data.data.case;
      } else toast.error(res.data?.message || "Failed");
    },
    async cancelCase() {
      const reason = window.prompt("Cancellation reason:");
      if (!reason || reason.trim().length < 3) return;
      const res = await this.api("clrCancelCase", { id: this.activeCase.id, reason });
      if (res.data?.success) {
        toast.success("Cancelled");
        this.activeCase = res.data.data.case;
      } else toast.error(res.data?.message || "Failed");
    },
    async downloadCertificate() {
      const res = await this.api("clrGetCertificate", { id: this.activeCase.id });
      if (!res.data?.success) return toast.error(res.data?.message || "No certificate");
      const c = res.data.data.certificate;
      const doc = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
      doc.setFontSize(16);
      doc.text("COUNTY GOVERNMENT OF HOMA BAY", 105, 24, { align: "center" });
      doc.setFontSize(13);
      doc.text("EMPLOYEE CLEARANCE CERTIFICATE", 105, 34, { align: "center" });
      doc.setFontSize(10);
      const lines = [
        `Certificate No: ${c.certificate_number}`,
        `Clearance No: ${c.clearance_number}`,
        `Employee: ${c.employee_name}`,
        `Personal No: ${c.emp_no}`,
        `Designation: ${c.designation || "—"}`,
        `Department: ${c.department_name || "—"}`,
        `Exit type: ${c.exit_type}`,
        `Effective exit: ${c.effective_exit_date || "—"}`,
        `Clearance date: ${c.cleared_at || "—"}`,
        `Liability position: ${c.final_liability_position || "—"}`,
        `Verification code: ${c.verification_code || "—"}`
      ];
      let y = 50;
      lines.forEach((ln) => {
        doc.text(ln, 20, y);
        y += 8;
      });
      doc.setFontSize(9);
      doc.text("Electronically verified via HRMIS. Verify with the certificate number or verification code.", 20, 250);
      doc.save(`${c.certificate_number || "clearance"}.pdf`);
      toast.success("Certificate PDF downloaded");
    },
    openWizard() {
      this.wizard = {
        open: true, step: 1, empNo: "", employee: null, exit_type: "",
        notice_date: "", last_working_date: "", effective_exit_date: "",
        reason_for_exit: "", exit_reference_no: "", release_letter_ref: "",
        additional_remarks: "", exception_duplicate: false, exception_reason: "",
        saving: false, error: ""
      };
      if (!this.exitTypes.length) this.loadConfig();
      this.$nextTick(() => {
        const el = this.$refs.wizardEmpInput;
        if (el && typeof el.focus === "function") el.focus();
      });
    },
    async searchEmp() {
      this.wizard.error = "";
      const res = await this.api("clrSearchEmployee", { empNo: this.wizard.empNo });
      if (res.data?.success) {
        this.wizard.employee = res.data.data.employee;
      } else {
        this.wizard.employee = null;
        this.wizard.error = res.data?.message || "Not found";
      }
    },
    nextWizard() {
      this.wizard.error = "";
      if (this.wizard.step === 1 && !this.wizard.employee) {
        this.wizard.error = "Search and select an employee first";
        return;
      }
      if (this.wizard.step === 2) {
        if (!this.wizard.exit_type || !this.wizard.last_working_date) {
          this.wizard.error = "Exit type and last working date are required";
          return;
        }
      }
      this.wizard.step += 1;
    },
    async createCase() {
      this.wizard.saving = true;
      this.wizard.error = "";
      const e = this.wizard.employee;
      try {
        const payload = {
          ...e,
          exit_type: this.wizard.exit_type,
          notice_date: this.wizard.notice_date,
          last_working_date: this.wizard.last_working_date,
          effective_exit_date: this.wizard.effective_exit_date,
          reason_for_exit: this.wizard.reason_for_exit,
          exit_reference_no: this.wizard.exit_reference_no,
          release_letter_ref: this.wizard.release_letter_ref,
          additional_remarks: this.wizard.additional_remarks,
          exception_duplicate: this.wizard.exception_duplicate ? "1" : "0",
          exception_reason: this.wizard.exception_reason
        };
        const res = await this.api("clrCreateCase", payload);
        if (res.data?.success) {
          toast.success("Clearance created: " + res.data.data.clearance_number);
          this.wizard.open = false;
          await this.openCase(res.data.data.id);
          this.loadDashboard();
        } else {
          this.wizard.error = res.data?.message || "Create failed";
        }
      } finally {
        this.wizard.saving = false;
      }
    },
    async saveStageConfig(s) {
      const res = await this.api("clrSaveStageConfig", {
        id: s.id,
        sla_days: s.sla_days,
        is_mandatory: s.is_mandatory,
        is_active: s.is_active,
        responsible_role: s.responsible_role,
        parallel_group: s.parallel_group
      });
      if (res.data?.success) toast.success("Saved");
      else toast.error(res.data?.message || "Failed");
    },
    async loadReport() {
      this.reportLoading = true;
      try {
        const res = await this.api("clrGetReports", { reportType: this.reportType });
        const rows = res.data?.success ? res.data.data?.rows || [] : [];
        this.reportRows = rows;
        this.reportHeaders = rows.length ? Object.keys(rows[0]) : [];
      } finally {
        this.reportLoading = false;
      }
    },
    exportReportCsv() {
      if (!this.reportRows.length) return;
      const body = this.reportRows.map((r) => this.reportHeaders.map((h) => r[h]));
      downloadCsv(this.reportHeaders, body, `clearance_${this.reportType}_${Date.now()}.csv`);
      toast.success("CSV exported");
    },
    async exportReportPdf() {
      if (!this.reportRows.length) return;
      this.reportLoading = true;
      try {
        const body = this.reportRows.map((r) => this.reportHeaders.map((h) => r[h]));
        await downloadCountyTablePdf({
          title: "EMPLOYEE CLEARANCE REPORT",
          subtitle: this.formatStatus(this.reportType),
          headers: this.reportHeaders,
          rows: body,
          filename: `clearance_${this.reportType}_${Date.now()}.pdf`,
          orientation: "landscape"
        });
        toast.success("PDF exported");
      } catch (e) {
        toast.error(e?.message || "PDF failed");
      } finally {
        this.reportLoading = false;
      }
    }
  }
};
</script>

<style scoped>
.clr-header-actions { display: flex; gap: 8px; flex-wrap: wrap; }
.clr-tabs, .clr-ws-tabs { display: flex; flex-wrap: wrap; gap: 8px; margin-bottom: 14px; }
.clr-tab {
  border: 1px solid #cbd5e1; background: #fff; padding: 8px 14px; border-radius: 999px;
  font-weight: 600; font-size: 13px; cursor: pointer; color: #334155; display: inline-flex; gap: 6px; align-items: center;
}
.clr-tab.on { background: #0c2d48; color: #fff; border-color: #0c2d48; }
.clr-section { margin-bottom: 20px; }
.clr-metrics {
  display: grid; grid-template-columns: repeat(auto-fill, minmax(140px, 1fr)); gap: 10px; margin-bottom: 14px;
}
.clr-metric {
  background: #fff; border: 1px solid #e2e8f0; border-radius: 12px; padding: 12px;
}
.clr-metric .lbl { display: block; font-size: 11px; font-weight: 700; color: #64748b; text-transform: uppercase; margin-bottom: 4px; }
.clr-metric strong { font-size: 20px; color: #0c2d48; }
.clr-metric strong.ok { color: #15803d; }
.clr-metric strong.bad { color: #b91c1c; }
.clr-card {
  background: #fff; border: 1px solid #e2e8f0; border-radius: 14px; padding: 14px 16px;
}
.clr-card-head { display: flex; justify-content: space-between; align-items: center; margin-bottom: 10px; }
.clr-card h4 { margin: 0 0 10px; color: #0c2d48; }
.clr-filters { display: flex; flex-wrap: wrap; gap: 8px; margin-bottom: 12px; align-items: center; }
.inp {
  border: 1px solid #cbd5e1; border-radius: 10px; padding: 8px 10px; font-size: 13px; background: #fff;
}
.inp.sm { padding: 5px 8px; font-size: 12px; }
.inp.ta { width: 100%; resize: vertical; font-family: inherit; }
.chk { font-size: 13px; color: #475569; display: inline-flex; gap: 6px; align-items: center; }
.table-wrap { overflow: auto; max-height: 620px; }
.clr-table { width: 100%; border-collapse: collapse; font-size: 13px; }
.clr-table th {
  text-align: left; background: #f8fafc; padding: 9px 8px; border-bottom: 1px solid #e2e8f0;
  color: #475569; font-weight: 700; white-space: nowrap; position: sticky; top: 0;
}
.clr-table td { padding: 9px 8px; border-bottom: 1px solid #f1f5f9; vertical-align: top; }
.clr-table.compact th, .clr-table.compact td { font-size: 12px; padding: 6px; }
.mono { font-variant-numeric: tabular-nums; }
.sub { font-size: 12px; color: #64748b; }
.badge {
  display: inline-block; padding: 2px 9px; border-radius: 999px; font-size: 11px; font-weight: 700;
  background: #f1f5f9; color: #475569; text-transform: capitalize;
}
.badge.ok { background: #ecfdf5; color: #047857; }
.badge.bad { background: #fef2f2; color: #b91c1c; }
.badge.warn { background: #fff7ed; color: #c2410c; }
.badge.lg { font-size: 12px; padding: 6px 12px; }
.link { background: none; border: none; color: #1d4ed8; font-weight: 700; cursor: pointer; padding: 0; }
.clr-empty { text-align: center; color: #64748b; padding: 28px 12px; }
.clr-muted { color: #64748b; margin: 0 0 10px; }
.clr-pipeline {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(160px, 1fr));
  gap: 8px;
  margin-top: 10px;
}
.clr-pipe-step {
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  padding: 10px;
  background: #f8fafc;
}
.clr-pipe-step.hot { background: #eff6ff; border-color: #93c5fd; }
.clr-pipe-step.overdue { background: #fef2f2; border-color: #fca5a5; }
.pipe-name { font-size: 12px; font-weight: 700; color: #0c2d48; min-height: 34px; }
.pipe-counts { font-size: 11px; color: #64748b; display: flex; flex-direction: column; gap: 2px; margin-top: 4px; }
.pipe-counts .bad, .overdue { color: #b91c1c; font-weight: 700; }
.link.warn { color: #c2410c; margin-left: 8px; }
.clr-actions-cell { white-space: nowrap; }
.clr-case-top {
  display: flex; flex-wrap: wrap; gap: 10px; align-items: center; margin-bottom: 14px;
  background: #fff; border: 1px solid #e2e8f0; border-radius: 14px; padding: 12px 14px;
}
.clr-case-top h3 { margin: 0; font-size: 16px; color: #0c2d48; }
.clr-case-top .grow { flex: 1 1 220px; }
.stepper {
  display: flex; gap: 8px; overflow-x: auto; padding-bottom: 10px; margin-bottom: 12px;
}
.step {
  min-width: 140px; max-width: 180px; background: #fff; border: 1px solid #e2e8f0; border-radius: 12px;
  padding: 10px; cursor: pointer; flex: 0 0 auto;
}
.step .dot { width: 10px; height: 10px; border-radius: 50%; background: #cbd5e1; display: inline-block; margin-bottom: 6px; }
.step.done .dot { background: #16a34a; }
.step.active .dot { background: #2563eb; }
.step.returned .dot, .step.overdue .dot { background: #ea580c; }
.step.selected { border-color: #0c2d48; box-shadow: 0 0 0 2px rgba(12,45,72,.15); }
.step .name { display: block; font-size: 12px; font-weight: 700; color: #0f172a; }
.step .st { font-size: 11px; color: #64748b; }
.clr-grid-2 { display: grid; grid-template-columns: 1fr 1fr; gap: 12px; }
.dl { display: grid; gap: 8px; margin: 0; }
.dl div { display: grid; grid-template-columns: 140px 1fr; gap: 8px; font-size: 13px; }
.dl dt { color: #64748b; font-weight: 600; margin: 0; }
.dl dd { margin: 0; color: #0f172a; }
.dl .money { font-weight: 700; }
.checklist { list-style: none; padding: 0; margin: 0; }
.checklist li { display: flex; gap: 8px; align-items: center; padding: 6px 0; border-bottom: 1px solid #f1f5f9; font-size: 13px; }
.checklist small { margin-left: auto; color: #64748b; }
.ok-i { color: #16a34a; }
.warn-i { color: #ea580c; }
.form-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 10px; }
.fld { display: flex; flex-direction: column; gap: 4px; font-size: 12px; font-weight: 600; color: #475569; }
.fld.span2, .form-grid .span2 { grid-column: 1 / -1; }
.btn-row { display: flex; flex-wrap: wrap; gap: 8px; margin-top: 12px; }
.approval-meta { margin-top: 12px; font-size: 12px; color: #64748b; }
.liab-form, .upload-row { display: flex; flex-wrap: wrap; gap: 8px; margin-bottom: 12px; align-items: center; }
.comment { border-top: 1px solid #f1f5f9; padding: 10px 0; }
.comment p { margin: 4px 0 0; font-size: 13px; }
.overdue { color: #b91c1c; font-weight: 700; }
/* Dedicated names — Bootstrap .modal is display:none and was freezing the UI on an empty dim layer */
.clr-overlay {
  position: fixed;
  inset: 0;
  background: rgba(15, 23, 42, 0.55);
  display: flex !important;
  align-items: center;
  justify-content: center;
  z-index: 10060;
  padding: 16px;
}
.clr-dialog {
  display: block !important;
  position: relative;
  background: #fff;
  border-radius: 16px;
  width: min(560px, 100%);
  max-height: 92vh;
  overflow: auto;
  box-shadow: 0 24px 60px rgba(0, 0, 0, 0.25);
  color: #0f172a;
}
.clr-dialog-wide { width: min(720px, 100%); }
.clr-dialog-head {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 18px;
  border-bottom: 1px solid #e2e8f0;
}
.clr-dialog-head h3 { margin: 0; color: #0c2d48; font-size: 1.1rem; }
.clr-icon-x {
  border: none;
  background: #f1f5f9;
  width: 32px;
  height: 32px;
  border-radius: 8px;
  font-size: 20px;
  cursor: pointer;
  line-height: 1;
}
.clr-dialog-body { padding: 16px 18px; }
.clr-dialog-foot {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  padding: 12px 18px;
  border-top: 1px solid #e2e8f0;
  flex-wrap: wrap;
}
.clr-wiz-steps { display: flex; gap: 8px; padding: 12px 18px 0; }
.clr-ws {
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background: #e2e8f0;
  color: #475569;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  font-size: 12px;
}
.clr-ws.on { background: #0c2d48; color: #fff; }
.inline { display: flex; gap: 8px; flex-wrap: wrap; }
.emp-card { background: #f8fafc; border-radius: 10px; padding: 12px; margin-top: 10px; }
.warn-text { color: #9a3412; margin-top: 8px; font-size: 13px; }
.error { color: #b91c1c; font-weight: 600; }
.route-list { font-size: 13px; padding-left: 18px; }

.clr-form-banner {
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  background: #ecfdf5;
  border: 1px solid #6ee7b7;
  border-radius: 12px;
  padding: 12px 14px;
  margin-bottom: 12px;
}
.clr-form-banner strong { color: #065f46; display: block; margin-bottom: 2px; }

/* Official liability form */
.clr-liability-form-wrap { background: #fafafa; }
.liability-form { max-width: 920px; margin: 0 auto; }
.liab-letterhead {
  text-align: center;
  border-bottom: 2px solid #0c2d48;
  padding-bottom: 12px;
  margin-bottom: 16px;
}
.liab-crest-line {
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.06em;
  color: #64748b;
  margin-bottom: 6px;
}
.liab-letterhead h3 {
  margin: 0;
  font-size: 15px;
  color: #0c2d48;
  letter-spacing: 0.04em;
}
.liab-letterhead h4 {
  margin: 4px 0;
  font-size: 13px;
  font-weight: 600;
  color: #334155;
}
.liab-addr { margin: 4px 0 10px; font-size: 12px; color: #64748b; }
.liab-title {
  display: inline-block;
  margin-top: 4px;
  padding: 6px 14px;
  background: #0c2d48;
  color: #fff;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.03em;
}
.liab-officer-block {
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  padding: 10px 14px;
  margin-bottom: 16px;
  background: #fff;
}
.liab-row {
  display: grid;
  grid-template-columns: 160px 1fr;
  gap: 8px;
  padding: 5px 0;
  border-bottom: 1px dotted #e2e8f0;
  font-size: 13px;
}
.liab-row:last-child { border-bottom: none; }
.liab-lbl { color: #64748b; font-weight: 600; }
.liab-val { color: #0f172a; font-weight: 600; border-bottom: 1px solid transparent; }
.liab-section-title {
  margin: 0 0 10px;
  font-size: 13px;
  color: #0c2d48;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}
.liab-prior {
  margin-bottom: 16px;
}
.liab-prior-card {
  border: 1px solid #dbeafe;
  background: #f8fafc;
  border-radius: 10px;
  padding: 10px 12px;
  margin-bottom: 8px;
}
.liab-prior-head {
  display: flex;
  justify-content: space-between;
  gap: 8px;
  align-items: center;
  margin-bottom: 4px;
}
.liab-answer-list {
  margin: 6px 0 0;
  padding-left: 16px;
  font-size: 12px;
  color: #334155;
}
.liab-answer-list span { font-weight: 600; color: #64748b; }
.liab-docs-mini { margin-top: 8px; display: flex; flex-wrap: wrap; gap: 6px; align-items: center; }
.liab-file-chip {
  border: 1px solid #93c5fd;
  background: #eff6ff;
  color: #1d4ed8;
  border-radius: 999px;
  padding: 4px 10px;
  font-size: 11px;
  font-weight: 600;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  max-width: 100%;
}
.liab-stage-block {
  border: 2px solid #0c2d48;
  border-radius: 12px;
  padding: 14px 16px;
  background: #fff;
}
.liab-in-charge {
  border-bottom: 1px solid #e2e8f0;
  margin-bottom: 12px;
  padding-bottom: 10px;
}
.liab-in-charge-label {
  display: inline-block;
  font-size: 10px;
  font-weight: 800;
  letter-spacing: 0.08em;
  color: #fff;
  background: #0c2d48;
  padding: 2px 8px;
  border-radius: 4px;
  margin-bottom: 6px;
}
.liab-in-charge h5 {
  margin: 4px 0;
  font-size: 14px;
  color: #0c2d48;
}
.liab-fields { margin-bottom: 14px; }
.req { color: #b91c1c; font-style: normal; font-weight: 700; }
.fld-hint { font-style: normal; font-weight: 500; color: #94a3b8; font-size: 11px; }
.liab-note {
  margin: 0;
  padding: 10px 12px;
  background: #fff7ed;
  border: 1px solid #fdba74;
  border-radius: 8px;
  color: #9a3412;
  font-size: 13px;
  font-weight: 600;
}
.liab-attach-panel {
  border: 1px dashed #94a3b8;
  border-radius: 10px;
  padding: 12px;
  margin: 12px 0;
  background: #f8fafc;
}
.liab-attach-panel h6 {
  margin: 0 0 6px;
  font-size: 13px;
  color: #0c2d48;
}
.liab-docs-list { margin-bottom: 10px; }
.liab-doc-row {
  display: flex;
  justify-content: space-between;
  gap: 10px;
  align-items: center;
  padding: 8px 0;
  border-bottom: 1px solid #e2e8f0;
  font-size: 13px;
}
.liab-upload-row { margin-bottom: 0; }
.liab-signature-line {
  margin: 14px 0 0;
  font-size: 12px;
  color: #64748b;
  letter-spacing: 0.02em;
}
@media (max-width: 900px) {
  .clr-grid-2, .form-grid { grid-template-columns: 1fr; }
  .dl div { grid-template-columns: 1fr; }
  .liab-row { grid-template-columns: 1fr; }
}
</style>
